import { Fragment } from "react";
import type { CustomerOrderItem } from "@shared-core/data/customer-api-types";
import {
  applyCarrierProgress,
  buildOrderActivity,
  type CarrierProgress,
} from "@shared-core/domain/customerOrderDetail";
import {
  itemDateFact,
  itemNextStep,
  itemSpecLine,
  pence,
} from "@shared-core/domain/customerOrderWithItems";
import {
  formatCount,
  formatQuotedPrice,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { cn } from "../../../app/components/primitives/utils";
import { DeliveryLink } from "../../customer-pages/DeliveryLink";
import { ItemHistory } from "../../customer-pages/ItemHistory";
import { ItemThumbnail } from "../../customer-pages/ItemThumbnail";
import { SubTabs, type SubTab } from "../../customer-pages/SubTabs";
import { Paperclip } from "lucide-react";

// One item of an order, chosen in the Items list: laid out as the CIS order page's item is (OrderItemDetail): its
// number and name, one line of spec, the next step ("→ Out for delivery today with DPD"), the same sub-tabs, and its
// dated History at the foot. Sub-tabs are [Details & quantity] always and [Artwork] where we hold the artwork file.
// Where the item is going and how to follow it is the order's Delivery tab (one place, P05): the details link there.
// The job's tracker is not drawn: the list says where it is in one line and the next step in another, and History
// dates the stops it has reached. The carrier's word on the item's parcel is read by the page (its consignment's
// progress), so the list, the banner and Complete agree with this pane.

export type ItemPaneId = "details" | "artwork";

export function availablePanes(item: CustomerOrderItem): ItemPaneId[] {
  return [
    "details",
    ...(item.artwork?.file_name ? (["artwork"] as const) : []),
  ];
}

export function resolveItemPane(
  requested: string | null,
  item: CustomerOrderItem,
): ItemPaneId {
  const panes = availablePanes(item);
  return panes.find((id) => id === requested) ?? "details";
}

const LABELS: Record<ItemPaneId, string> = {
  details: "Details & quantity",
  artwork: "Artwork",
};

export function OrderItemPane({
  item,
  itemNumber,
  pane,
  onPane,
  onOpenDelivery,
  carrier,
  deliveryAddresses,
  orderedAt,
  orderFacts,
}: {
  item: CustomerOrderItem;
  /** "01": the customer's number for the item. */
  itemNumber: string;
  pane: ItemPaneId;
  onPane: (pane: ItemPaneId) => void;
  /** The carrier's word on this item's parcel, once the page has read it: our milestones lag the carrier's. */
  carrier: CarrierProgress | null;
  /** Open the order's Delivery tab, which lists every item's delivery. */
  onOpenDelivery: () => void;
  /** Every address the item is going to or has gone to, from the order's Delivery tab. */
  deliveryAddresses: string[];
  orderedAt: string | null | undefined;
  /** The order's own facts that belong with an item's details: order number, site, who placed it. */
  orderFacts: Array<{ label: string; value: string | null | undefined }>;
}) {
  const stops = item.progress
    ? applyCarrierProgress(
        item.progress.stepper ?? [],
        carrier,
        item.delivery?.delivered_on,
      )
    : [];
  const history = buildOrderActivity({
    stops,
    invoices: item.invoices ?? [],
    orderedAt,
  }).map((event) => ({ at: event.at, text: event.text }));
  const specLine = itemSpecLine(item);
  const nextStep = carrier?.headline ?? itemNextStep(item);
  const date = itemDateFact(item, stops, carrier);
  const panes = availablePanes(item);
  const tabs: Array<SubTab<ItemPaneId>> = panes.map((id) => ({
    id,
    label: LABELS[id],
    needsYou: false,
  }));
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    ...(item.quantity != null
      ? [{ label: "Quantity", value: formatCount(item.quantity) }]
      : []),
    ...(item.spec ?? []).map((row) => ({
      label: row.label,
      value: row.value,
    })),
    ...(date
      ? [
          {
            label: date.label,
            value: (
              <span
                className={cn(
                  date.tone === "late" && "text-red-700 dark:text-red-400",
                  date.tone === "done" && "text-green-700 dark:text-green-400",
                )}
              >
                {formatWeekdayDateOrDash(date.date)}
              </span>
            ),
          },
        ]
      : []),
    {
      label: "Price",
      value: (
        <>
          {formatQuotedPrice(pence(item.net_amount))}
          <span className="text-muted-foreground"> + VAT</span>
        </>
      ),
    },
    ...(item.job_no ? [{ label: "Job", value: item.job_no }] : []),
    ...orderFacts
      .filter((fact) => fact.value)
      .map((fact) => ({ label: fact.label, value: fact.value })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <ItemThumbnail
          name={item.description}
          family={item.product_name}
          imageUrl={item.artwork?.thumbnail_url}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-lg font-semibold break-words">
            <span className="text-muted-foreground tabular-nums">
              {itemNumber}
            </span>{" "}
            {item.description}
          </h3>
          {specLine && (
            <p className="text-sm text-muted-foreground">{specLine}</p>
          )}
          {nextStep && <p className="text-sm font-medium">→ {nextStep}</p>}
        </div>
      </div>

      {tabs.length > 1 && (
        <SubTabs tabs={tabs} current={pane} onSelect={onPane} isWrapping />
      )}

      {pane === "details" && (
        <div className="space-y-4">
          <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
            {rows.map((row) => (
              <Fragment key={row.label}>
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="break-words tabular-nums">{row.value}</dd>
              </Fragment>
            ))}
          </dl>
          <DeliveryLink
            addressNames={deliveryAddresses}
            onOpen={onOpenDelivery}
          />
        </div>
      )}

      {pane === "artwork" && item.artwork?.file_name && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Paperclip className="size-4 shrink-0" />
          <span className="break-words">Artwork: {item.artwork.file_name}</span>
        </p>
      )}

      <ItemHistory lines={history} />
    </div>
  );
}
